/**
 * Sign-in, matching auth_phone/views.py and auth_phone/email_views.py.
 *
 * No phone-number sign-in: the web app sends no SMS, and the server refuses
 * a browser one when the app check is enforced (`auth_phone/appcheck.py`).
 *
 * Email:
 *   GET  /v1/auth/methods/                               -> { phone, email, email_signup, google, google_client_id }
 *   POST /v1/auth/email/start   { email, locale }        -> { sent, expires_in_seconds, resend_after_seconds }
 *   POST /v1/auth/email/verify  { email, code }          -> { token, user, tenant }
 *   POST /v1/auth/phone/lock/verify { email|phone|provider, challenge, pin } -> the same payload
 *
 * Google (`auth_phone/google_auth.py`):
 *   POST /v1/auth/google/nonce/                          -> { nonce, client_id }
 *   POST /v1/auth/google/verify/ { id_token }            -> { token, user, tenant }
 *
 * Session:
 *   POST /v1/auth/logout/                                -> { revoked }
 *
 * The phone `start` answers 200 even for a number that is invalid or in
 * cooldown, on purpose: telling the caller which is which leaks who has been
 * hammering which number. The email `start` is the same shape and the same
 * promise - it never looks an account up - so what an address MEANS (an
 * existing account, a new one, sign-up closed) is only revealed by `verify`, to
 * somebody who has just proved they read that inbox.
 *
 * Every unauthenticated call here carries the app-check header when a build
 * has one (`appCheck.ts`); the server only asks for it when it has a secret.
 */
import { api, ApiError } from './api';
import { appCheckHeaders } from './appCheck';
import { getSession, setSession, type Session } from './session';

type VerifyResponse = {
  token: string;
  user: Session['user'] & {
    is_new: boolean;
    /** True while this account still owes a date of birth. Absent from a
     *  server that predates it, which reads as "nothing owed". */
    needs_date_of_birth?: boolean;
  };
  tenant: { slug: string };
};

/** What a successful sign-in hands back. */
export type SignInResult = {
  session: Session;
  /** The account was created by this sign-in. */
  isNew: boolean;
};

/** Thrown when the account has a registration-lock PIN set. */
export class RegistrationLockError extends Error {
  constructor(
    readonly retryAfterSeconds: number,
    /** Spent against the PIN at `/v1/auth/phone/lock/verify/`. Empty when the
     *  server did not send one, in which case there is no PIN step to offer. */
    readonly challenge: string = '',
  ) {
    super('This account is protected by a registration PIN.');
    this.name = 'RegistrationLockError';
  }
}

/**
 * The 401 `registration_lock_required` answer, or null for anything else.
 *
 * "You need your PIN" is a STEP in the flow, not a failure of it, so it is
 * recognised before the generic error path turns it into "that code didn't
 * work" - which would send somebody off to request another code that would
 * land them right back here.
 */
export function readLockChallenge(err: unknown): RegistrationLockError | null {
  if (!(err instanceof ApiError) || err.status !== 401) return null;
  const body = err.body as
    | { registration_lock_required?: boolean; challenge?: unknown; retry_after_seconds?: unknown }
    | undefined;
  if (!body?.registration_lock_required) return null;
  return new RegistrationLockError(
    Number(body.retry_after_seconds) || 0,
    typeof body.challenge === 'string' ? body.challenge : '',
  );
}

/**
 * The stored session for a sign-in payload.
 *
 * A new account, or one with no name, owes a name; one the server says has no
 * date of birth owes that too. Both ride on the session as `onboarding` so the
 * app can hold them at the profile step until they are answered.
 */
export function sessionFromSignIn(res: VerifyResponse): Session {
  const needsName = res.user.is_new || !(res.user.display_name ?? '').trim();
  const needsDob = res.user.needs_date_of_birth === true;
  return {
    token: res.token,
    tenantSlug: res.tenant.slug,
    user: {
      id: res.user.id,
      display_name: res.user.display_name,
      avatar_url: res.user.avatar_url,
      external_user_id: res.user.external_user_id,
    },
    ...(needsName || needsDob ? { onboarding: { name: needsName, dob: needsDob } } : {}),
  };
}

function finishSignIn(res: VerifyResponse): SignInResult {
  const session = sessionFromSignIn(res);
  setSession(session);
  return { session, isNew: res.user.is_new === true };
}

// ── which doors are open ──────────────────────────────────────────────

/** Which ways in the server offers (`GET /v1/auth/methods/`). */
export type AuthMethods = {
  phone: boolean;
  email: boolean;
  /** False whenever `email` is: new accounts by email are a second switch. */
  email_signup: boolean;
  /** "Continue with Google": the staff switch AND a web client to sign in as. */
  google: boolean;
  /** The web OAuth client Google's button signs in as. Empty while `google` is off. */
  google_client_id: string;
};

/** Email OFF. The state until the server has said otherwise: a button that
 *  leads to a form the server answers with a 404 is worse than a missing one. */
export const DEFAULT_METHODS: AuthMethods = {
  phone: true,
  email: false,
  email_signup: false,
  google: false,
  google_client_id: '',
};

/** A body from the wire as a safe value. */
export function coerceMethods(data: unknown): AuthMethods {
  const d = (data ?? {}) as Partial<Record<keyof AuthMethods, unknown>>;
  const email = d.email === true;
  const clientId = typeof d.google_client_id === 'string' ? d.google_client_id.trim() : '';
  // A switch with no client to sign in as can only draw a button that fails.
  const google = d.google === true && clientId !== '';
  return {
    phone: d.phone !== false,
    email,
    // The server already makes this false whenever `email` is; holding it
    // here too means a stale or hand-edited body cannot say otherwise.
    email_signup: email && d.email_signup === true,
    google,
    google_client_id: google ? clientId : '',
  };
}

/**
 * Ask the server which sign-in methods are open.
 *
 * A 404 (a server without the endpoint) is an answer - email is off. Every
 * other failure throws, and the caller keeps its default of hiding email.
 */
export async function fetchMethods(signal?: AbortSignal): Promise<AuthMethods> {
  try {
    const data = await api<unknown>('/v1/auth/methods/', {
      anonymous: true,
      signal,
      headers: appCheckHeaders(),
    });
    return coerceMethods(data);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return { ...DEFAULT_METHODS };
    throw err;
  }
}

// ── email ─────────────────────────────────────────────────────────────

/** Only the floor, and only when the server sent nothing: a backend that
 *  predates the resend ladder. The real wait escalates per address - ten
 *  minutes before the second code, six hours before the third - so the
 *  countdown has to come from the response. */
export const DEFAULT_RESEND_SECONDS = 60;

export type CodeSent = {
  expiresInSeconds: number;
  /** How long until another code may be asked for. */
  resendAfterSeconds: number;
};

/**
 * Email a six-digit code. The answer is the same whether or not the address
 * has an account.
 *
 * Takes the locale because the message is written in it and this is the one
 * request where the server cannot work the language out for itself: there is
 * no account yet to read a stored choice from, and the browser's
 * `Accept-Language` is the language of the BROWSER rather than the one the
 * page is being read in.
 */
export async function startEmail(email: string, locale: string): Promise<CodeSent> {
  const res = await api<{ expires_in_seconds?: number; resend_after_seconds?: number }>(
    '/v1/auth/email/start/',
    {
      method: 'POST',
      anonymous: true,
      headers: appCheckHeaders(),
      body: { email, locale },
    },
  );
  const resend = Number(res?.resend_after_seconds);
  return {
    expiresInSeconds: Number(res?.expires_in_seconds) || 600,
    resendAfterSeconds: Number.isFinite(resend) && resend >= 0 ? resend : DEFAULT_RESEND_SECONDS,
  };
}

/**
 * Verify an emailed code and sign in.
 *
 * Sends NO date of birth and NO country: the age question comes after the
 * identity is proven, and only to an account that owes it (a mistyped year
 * sent here would lock the ADDRESS, `age_gate.check` runs before the account
 * exists), and a browser has no device region to offer - a language tag like
 * `en-US` says what the reader prefers, not where they are, and the server
 * falls back to its own GeoIP header and the account's timezone.
 *
 * Throws `RegistrationLockError` for an account with a PIN; the PIN step is
 * `verifyLockPin`. Every other failure is the `ApiError` as it came.
 */
export async function verifyEmail(email: string, code: string): Promise<SignInResult> {
  try {
    const res = await api<VerifyResponse>('/v1/auth/email/verify/', {
      method: 'POST',
      anonymous: true,
      headers: appCheckHeaders(),
      body: { email, code },
    });
    return finishSignIn(res);
  } catch (err) {
    throw readLockChallenge(err) ?? err;
  }
}

// ── Google ────────────────────────────────────────────────────────────

/**
 * A single-use nonce for one Google sign-in, and the client to sign in as.
 *
 * The nonce goes into Google's button and comes back inside the ID token,
 * which is what ties the token to a sign-in this server started minutes ago:
 * a token minted for our client somewhere else does not carry it.
 */
export async function googleNonce(signal?: AbortSignal): Promise<{ nonce: string; clientId: string }> {
  const res = await api<{ nonce?: string; client_id?: string }>('/v1/auth/google/nonce/', {
    method: 'POST',
    anonymous: true,
    signal,
    headers: appCheckHeaders(),
    body: {},
  });
  return { nonce: String(res?.nonce ?? ''), clientId: String(res?.client_id ?? '') };
}

/**
 * Exchange Google's ID token for a session. Same answers as `verifyEmail`:
 * `RegistrationLockError` for an account with a PIN (answered with
 * `verifyLockPin({ provider: 'google' }, ...)`), the `ApiError` otherwise.
 * No date of birth and no country, for the reasons `verifyEmail` gives.
 */
export async function verifyGoogle(idToken: string): Promise<SignInResult> {
  try {
    const res = await api<VerifyResponse>('/v1/auth/google/verify/', {
      method: 'POST',
      anonymous: true,
      headers: appCheckHeaders(),
      body: { id_token: idToken },
    });
    return finishSignIn(res);
  } catch (err) {
    throw readLockChallenge(err) ?? err;
  }
}

/** Who a PIN challenge was issued to: an address, or a provider (whose
 *  challenge alone names the identity, `auth_phone/views.py`). */
export type LockIdentity = { email: string } | { provider: 'google' };

/**
 * Complete a sign-in that stopped at the PIN.
 *
 * Answers on the SAME identity the code was proved for: a challenge comes from
 * one of the two verify endpoints and is spent against that identity's own
 * code. A wrong PIN costs an attempt against the lock's own ladder and never
 * burns the code.
 */
export async function verifyLockPin(
  identity: LockIdentity,
  challenge: string,
  pin: string,
): Promise<SignInResult> {
  const res = await api<VerifyResponse>('/v1/auth/phone/lock/verify/', {
    method: 'POST',
    anonymous: true,
    headers: appCheckHeaders(),
    body: { ...identity, challenge, pin },
  });
  return finishSignIn(res);
}

// ── ending a session ──────────────────────────────────────────────────

/** Sign-out must never wait on a slow network. */
export const LOGOUT_TIMEOUT_MS = 4000;

/**
 * Tell the server this session is over: `POST /v1/auth/logout/` revokes the
 * session the TOKEN names, so the token is a 401 from the next request on.
 *
 * Best effort, and it never throws: a browser that is offline at sign-out
 * still clears locally, and the token is only good for its own 30 days. It
 * must run BEFORE the local session is cleared - the call is authenticated by
 * that token, and one sent after would go out with nothing and revoke nothing.
 * A device-link token (QR) has no session id and is answered
 * `revoked: false`, which is the same outcome as far as this page is concerned.
 */
export async function logoutFromServer(
  timeoutMs: number = LOGOUT_TIMEOUT_MS,
  /** The token being signed out. When given, nothing is sent unless it is
   *  still THE session: a call that was queued behind a slow request must not
   *  revoke the account somebody has since signed into. */
  expectedToken?: string,
): Promise<boolean> {
  const current = getSession();
  if (!current) return false;
  if (expectedToken !== undefined && current.token !== expectedToken) return false;
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), timeoutMs);
  try {
    const res = await api<{ revoked?: boolean }>('/v1/auth/logout/', {
      method: 'POST',
      body: {},
      signal: ac.signal,
    });
    return res?.revoked === true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
